'use client'

import { motion, AnimatePresence } from 'framer-motion'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { CardWatermark } from '@/components/ui/card-watermark'
import { Icons } from '@/components/ui/icons'
import { cn } from '@/lib/utils'
import { Skeleton } from '@/components/ui/skeleton'
import { useState } from 'react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'

interface Agent {
  agent_name: string
  call_count: number
  average_score: number
  grade: 'A' | 'B' | 'C' | 'D'
  total_fatal_issues: number
  positive_outcomes?: number
  negative_outcomes?: number
  neutral_outcomes?: number
  avg_talk_ratio?: number | null
  avg_call_duration?: number | null
}

interface AgentWiseViewProps {
  agents: Agent[]
  isLoading?: boolean
}

const GRADE_COLORS: Record<string, string> = {
  A: 'bg-emerald-500 text-white',
  B: 'bg-blue-500 text-white',
  C: 'bg-amber-500 text-white',
  D: 'bg-red-500 text-white',
}

const GRADE_BAR: Record<string, string> = {
  A: 'bg-emerald-500',
  B: 'bg-blue-500',
  C: 'bg-amber-500',
  D: 'bg-red-500',
}

const AVATAR_COLORS = [
  'bg-blue-500', 'bg-purple-500', 'bg-emerald-500', 'bg-orange-500',
  'bg-pink-500', 'bg-teal-500', 'bg-rose-500', 'bg-indigo-500',
  'bg-cyan-500', 'bg-violet-500',
]

function getInitials(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(w => w[0].toUpperCase())
    .join('')
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = Math.round(seconds % 60)
  return `${m}m ${s}s`
}

function SkeletonRow() {
  return (
    <div className='flex items-center gap-4 px-5 py-4 border-b'>
      <Skeleton className='h-10 w-10 rounded-full shrink-0' />
      <div className='flex-1 space-y-1.5'>
        <Skeleton className='h-3.5 w-36' />
        <Skeleton className='h-3 w-20' />
      </div>
      <Skeleton className='h-7 w-7 rounded-full shrink-0' />
      <div className='w-40 space-y-1.5'>
        <Skeleton className='h-3 w-24' />
        <Skeleton className='h-2 w-full rounded-full' />
      </div>
      <div className='w-40 space-y-1.5'>
        <Skeleton className='h-3 w-28' />
        <Skeleton className='h-2 w-full rounded-full' />
      </div>
      <div className='w-20 space-y-1'>
        <Skeleton className='h-4 w-14' />
        <Skeleton className='h-3 w-12' />
      </div>
      <Skeleton className='h-6 w-6 rounded' />
    </div>
  )
}

export function AgentWiseView({ agents, isLoading = false }: AgentWiseViewProps) {
  const [searchTerm, setSearchTerm] = useState('')
  const [sortBy, setSortBy] = useState<'score' | 'calls' | 'name'>('score')
  const [gradeFilter, setGradeFilter] = useState<'all' | 'A' | 'B' | 'C' | 'D'>('all')
  const [fatalFilter, setFatalFilter] = useState<'all' | 'zero' | 'some'>('all')
  const [expandedAgent, setExpandedAgent] = useState<string | null>(null)

  const overallStats = {
    totalAgents: agents.length,
    avgScore: agents.length
      ? Math.round(agents.reduce((s, a) => s + a.average_score, 0) / agents.length)
      : 0,
    totalCalls: agents.reduce((s, a) => s + a.call_count, 0),
    totalFatalIssues: agents.reduce((s, a) => s + a.total_fatal_issues, 0),
    gradeDistribution: {
      A: agents.filter(a => a.grade === 'A').length,
      B: agents.filter(a => a.grade === 'B').length,
      C: agents.filter(a => a.grade === 'C').length,
      D: agents.filter(a => a.grade === 'D').length,
    },
    totalPositiveOutcomes: agents.reduce((s, a) => s + (a.positive_outcomes ?? 0), 0),
  }

  const filteredAgents = agents
    .filter(agent => {
      const matchesSearch = agent.agent_name.toLowerCase().includes(searchTerm.toLowerCase())
      const matchesGrade = gradeFilter === 'all' || agent.grade === gradeFilter
      const matchesFatal =
        fatalFilter === 'all' ||
        (fatalFilter === 'zero' && agent.total_fatal_issues === 0) ||
        (fatalFilter === 'some' && agent.total_fatal_issues > 0)
      return matchesSearch && matchesGrade && matchesFatal
    })
    .sort((a, b) => {
      if (sortBy === 'score') return b.average_score - a.average_score
      if (sortBy === 'calls') return b.call_count - a.call_count
      return a.agent_name.localeCompare(b.agent_name)
    })

  const hasData = agents.length > 0

  return (
    <div className='space-y-5'>
      {/* Overall Team Stats */}
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
        <Card className='relative overflow-hidden'>
          <CardWatermark opacity={2} />
          <CardHeader className='relative z-10 pb-3'>
            <CardTitle className='flex items-center gap-2 text-base'>
              <Icons.barChart className='h-4 w-4 text-brand-cornflower' strokeWidth={1.5} />
              Overall Team Analysis
            </CardTitle>
          </CardHeader>
          <CardContent className='relative z-10'>
            <div className='grid gap-3 sm:grid-cols-2 lg:grid-cols-4'>
              <div className='rounded-xl border border-gray-100 bg-gradient-to-br from-brand-navy/5 to-brand-purple/5 p-4'>
                <div className='flex items-center gap-2 mb-1'>
                  <Icons.users className='h-3.5 w-3.5 text-brand-navy' />
                  <span className='text-xs text-muted-foreground'>Total Agents</span>
                </div>
                <p className='text-2xl font-bold text-brand-navy'>{overallStats.totalAgents}</p>
              </div>
              <div className='rounded-xl border border-emerald-100 bg-gradient-to-br from-emerald-50 to-emerald-100/40 p-4'>
                <div className='flex items-center gap-2 mb-1'>
                  <Icons.activity className='h-3.5 w-3.5 text-emerald-600' />
                  <span className='text-xs text-muted-foreground'>Team Avg Score</span>
                </div>
                <p className='text-2xl font-bold text-emerald-600'>{overallStats.avgScore}%</p>
              </div>
              <div className='rounded-xl border border-blue-100 bg-gradient-to-br from-blue-50 to-blue-100/40 p-4'>
                <div className='flex items-center gap-2 mb-1'>
                  <Icons.messageSquare className='h-3.5 w-3.5 text-blue-600' />
                  <span className='text-xs text-muted-foreground'>Total Calls</span>
                </div>
                <p className='text-2xl font-bold text-blue-600'>{overallStats.totalCalls}</p>
              </div>
              <div className='rounded-xl border border-red-100 bg-gradient-to-br from-red-50 to-red-100/40 p-4'>
                <div className='flex items-center gap-2 mb-1'>
                  <Icons.alertCircle className='h-3.5 w-3.5 text-red-600' />
                  <span className='text-xs text-muted-foreground'>Total Fatal Issues</span>
                </div>
                <p className='text-2xl font-bold text-red-600'>{overallStats.totalFatalIssues}</p>
              </div>
            </div>

            {hasData && (
              <div className='mt-4 flex flex-wrap items-center gap-4'>
                <span className='text-xs text-muted-foreground whitespace-nowrap'>Grade split</span>
                <div className='flex flex-wrap gap-3'>
                  {(['A', 'B', 'C', 'D'] as const).map(g => (
                    <div key={g} className='flex items-center gap-1.5'>
                      <span className={cn('h-2.5 w-2.5 rounded-full', GRADE_BAR[g])} />
                      <span className='text-xs text-muted-foreground'>
                        {g}: <strong className='text-foreground'>{overallStats.gradeDistribution[g]}</strong>
                      </span>
                    </div>
                  ))}
                </div>
                <div className='flex items-center gap-1.5 text-xs text-emerald-600 ml-auto'>
                  <Icons.trendingUp className='h-3.5 w-3.5' />
                  <strong>{overallStats.totalPositiveOutcomes}</strong>
                  <span className='text-muted-foreground'>positive outcomes</span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Agent List Table */}
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05 }}
      >
        <Card className='relative overflow-hidden'>
          <CardWatermark opacity={2} />

          {/* Table header bar */}
          <div className='relative z-10 flex items-center justify-between px-5 pt-4 pb-3 border-b'>
            <div>
              <h2 className='font-bold text-brand-navy'>Agent List</h2>
              <p className='text-xs text-muted-foreground mt-0.5'>
                Showing {filteredAgents.length} of {agents.length} agents
              </p>
            </div>

            {/* Filters inline */}
            <div className='flex items-center gap-2'>
              <div className='relative'>
                <Icons.search className='absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground' />
                <Input
                  placeholder='Search agents...'
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className='pl-8 h-8 text-sm w-44'
                />
              </div>

              <select
                value={sortBy}
                onChange={e => setSortBy(e.target.value as typeof sortBy)}
                className='rounded-lg border border-input bg-background px-2.5 py-1.5 text-xs h-8'
              >
                <option value='score'>Sort by Score</option>
                <option value='calls'>Sort by Calls</option>
                <option value='name'>Sort by Name</option>
              </select>

              <select
                value={gradeFilter}
                onChange={e => setGradeFilter(e.target.value as typeof gradeFilter)}
                className='rounded-lg border border-input bg-background px-2.5 py-1.5 text-xs h-8'
              >
                <option value='all'>All Grades</option>
                <option value='A'>Grade A</option>
                <option value='B'>Grade B</option>
                <option value='C'>Grade C</option>
                <option value='D'>Grade D</option>
              </select>

              <select
                value={fatalFilter}
                onChange={e => setFatalFilter(e.target.value as typeof fatalFilter)}
                className='rounded-lg border border-input bg-background px-2.5 py-1.5 text-xs h-8'
              >
                <option value='all'>All Agents</option>
                <option value='zero'>No Fatal Issues</option>
                <option value='some'>Has Fatal Issues</option>
              </select>

              {(searchTerm || gradeFilter !== 'all' || fatalFilter !== 'all') && (
                <Button
                  variant='ghost'
                  size='sm'
                  onClick={() => { setSearchTerm(''); setGradeFilter('all'); setFatalFilter('all') }}
                  className='h-8 px-2 text-xs'
                >
                  <Icons.close className='h-3 w-3 mr-1' />
                  Clear
                </Button>
              )}
            </div>
          </div>

          {/* Column headers */}
          {hasData && filteredAgents.length > 0 && (
            <div className='relative z-10 grid items-center px-5 py-2 bg-gray-50/60 border-b text-[11px] font-semibold uppercase tracking-wide text-muted-foreground'
              style={{ gridTemplateColumns: '2fr 80px 180px 200px 110px 48px' }}
            >
              <span>Agent Name / ID</span>
              <span>Grade</span>
              <span>Avg Score</span>
              <span>Sentiment</span>
              <span>Talk %</span>
              <span>Actions</span>
            </div>
          )}

          {/* Rows */}
          <div className='relative z-10 divide-y divide-gray-100'>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)
            ) : !hasData ? (
              <div className='flex flex-col items-center justify-center py-14 text-center'>
                <Icons.users className='mb-3 h-10 w-10 text-gray-200' />
                <h3 className='font-semibold text-brand-navy'>No agent data yet</h3>
                <p className='mt-1 text-sm text-muted-foreground max-w-xs'>
                  Agent performance will appear here once calls are assigned to agents and analyzed.
                </p>
              </div>
            ) : filteredAgents.length === 0 ? (
              <div className='flex flex-col items-center justify-center py-14'>
                <Icons.search className='mb-3 h-10 w-10 text-gray-200' />
                <h3 className='font-semibold text-brand-navy'>No results</h3>
                <p className='mt-1 text-sm text-muted-foreground'>Try adjusting your filters.</p>
              </div>
            ) : (
              filteredAgents.map((agent, index) => {
                const isExpanded = expandedAgent === agent.agent_name
                const pos = agent.positive_outcomes ?? 0
                const neg = agent.negative_outcomes ?? 0
                const neu = agent.neutral_outcomes ?? 0
                const total = pos + neg + neu || 1
                const posW = Math.round((pos / total) * 100)
                const neuW = Math.round((neu / total) * 100)
                const negW = 100 - posW - neuW
                const avatarColor = AVATAR_COLORS[index % AVATAR_COLORS.length]
                const initials = getInitials(agent.agent_name)

                return (
                  <div key={agent.agent_name}>
                    {/* Main row */}
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: index * 0.03 }}
                      className={cn(
                        'grid items-center px-5 py-3.5 transition-colors cursor-pointer',
                        'hover:bg-gray-50/70',
                        isExpanded && 'bg-blue-50/30'
                      )}
                      style={{ gridTemplateColumns: '2fr 80px 180px 200px 110px 48px' }}
                      onClick={() => setExpandedAgent(isExpanded ? null : agent.agent_name)}
                    >
                      {/* Agent name + avatar */}
                      <div className='flex items-center gap-3 min-w-0'>
                        <div className={cn(
                          'flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white',
                          avatarColor
                        )}>
                          {initials}
                        </div>
                        <div className='min-w-0'>
                          <p className='font-semibold text-brand-navy text-sm truncate'>{agent.agent_name}</p>
                          <p className='text-xs text-muted-foreground'>
                            {agent.call_count} call{agent.call_count !== 1 ? 's' : ''}
                          </p>
                        </div>
                      </div>

                      {/* Grade badge */}
                      <div>
                        <span className={cn(
                          'inline-flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold',
                          GRADE_COLORS[agent.grade]
                        )}>
                          {agent.grade}
                        </span>
                      </div>

                      {/* Avg score */}
                      <div className='pr-4'>
                        <div className='flex items-center justify-between mb-1'>
                          <span className='text-[11px] text-muted-foreground'>Performance</span>
                          <span className='text-sm font-bold text-brand-navy'>{agent.average_score}%</span>
                        </div>
                        <div className='h-2 w-full overflow-hidden rounded-full bg-gray-100'>
                          <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${agent.average_score}%` }}
                            transition={{ duration: 0.6, delay: index * 0.04 }}
                            className={cn('h-full rounded-full', GRADE_BAR[agent.grade])}
                          />
                        </div>
                      </div>

                      {/* Sentiment */}
                      <div className='pr-4'>
                        <div className='flex items-center gap-2 mb-1 text-[11px] text-muted-foreground'>
                          <span className='text-emerald-600 font-medium'>{pos} pos</span>
                          <span>{neu} neu</span>
                          <span className='text-red-500 font-medium'>{neg} neg</span>
                        </div>
                        {(pos + neg + neu) > 0 && (
                          <div className='h-2 w-full flex overflow-hidden rounded-full'>
                            {posW > 0 && (
                              <div className='h-full bg-emerald-400' style={{ width: `${posW}%` }} />
                            )}
                            {neuW > 0 && (
                              <div className='h-full bg-gray-300' style={{ width: `${neuW}%` }} />
                            )}
                            {negW > 0 && (
                              <div className='h-full bg-red-400' style={{ width: `${negW}%` }} />
                            )}
                          </div>
                        )}
                      </div>

                      {/* Talk % */}
                      <div>
                        {agent.avg_talk_ratio != null ? (
                          <div className='flex items-center gap-1 text-sm font-semibold text-brand-navy mb-0.5'>
                            <Icons.phone className='h-3.5 w-3.5 text-muted-foreground' />
                            {agent.avg_talk_ratio}%
                          </div>
                        ) : (
                          <span className='text-xs text-muted-foreground'>—</span>
                        )}
                        <div className='flex items-center gap-1 text-xs'>
                          <Icons.alertCircle className={cn(
                            'h-3 w-3',
                            agent.total_fatal_issues > 0 ? 'text-red-500' : 'text-emerald-500'
                          )} />
                          <span className={cn(
                            'font-medium',
                            agent.total_fatal_issues > 0 ? 'text-red-500' : 'text-muted-foreground'
                          )}>
                            {agent.total_fatal_issues} Fatal
                          </span>
                        </div>
                      </div>

                      {/* Expand chevron */}
                      <div className='flex justify-center'>
                        <div className={cn(
                          'flex h-7 w-7 items-center justify-center rounded-lg transition-colors',
                          isExpanded ? 'bg-brand-navy text-white' : 'bg-gray-100 text-muted-foreground hover:bg-gray-200'
                        )}>
                          {isExpanded
                            ? <Icons.chevronUp className='h-4 w-4' />
                            : <Icons.chevronDown className='h-4 w-4' />
                          }
                        </div>
                      </div>
                    </motion.div>

                    {/* Expanded detail row */}
                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.22 }}
                          className='overflow-hidden bg-gray-50/60 border-t border-blue-100'
                        >
                          <div className='grid gap-6 px-6 py-5 md:grid-cols-2'>
                            {/* Left: Outcome Breakdown */}
                            <div>
                              <h4 className='text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3'>
                                Outcome Breakdown
                              </h4>
                              <div className='space-y-3'>
                                {[
                                  { label: 'Positive Outcomes', value: pos, total: pos + neg + neu, color: 'bg-emerald-500', text: 'text-emerald-600' },
                                  { label: 'Neutral Outcomes', value: neu, total: pos + neg + neu, color: 'bg-gray-400', text: 'text-gray-500' },
                                  { label: 'Negative Outcomes', value: neg, total: pos + neg + neu, color: 'bg-red-500', text: 'text-red-500' },
                                ].map(item => {
                                  const pct = item.total > 0 ? Math.round((item.value / item.total) * 100) : 0
                                  return (
                                    <div key={item.label}>
                                      <div className='flex items-center justify-between mb-1'>
                                        <span className='text-xs text-muted-foreground'>{item.label}</span>
                                        <span className={cn('text-sm font-bold', item.text)}>{pct}%</span>
                                      </div>
                                      <div className='h-2 w-full overflow-hidden rounded-full bg-gray-200'>
                                        <motion.div
                                          initial={{ width: 0 }}
                                          animate={{ width: `${pct}%` }}
                                          transition={{ duration: 0.5 }}
                                          className={cn('h-full rounded-full', item.color)}
                                        />
                                      </div>
                                    </div>
                                  )
                                })}
                              </div>
                            </div>

                            {/* Right: Quick stats */}
                            <div>
                              <h4 className='text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3'>
                                Performance Summary
                              </h4>
                              <div className='grid grid-cols-2 gap-3'>
                                <div className='rounded-lg bg-white border border-gray-100 p-3'>
                                  <p className='text-xs text-muted-foreground'>Avg Score</p>
                                  <p className='text-xl font-bold text-brand-navy mt-0.5'>{agent.average_score}%</p>
                                </div>
                                <div className='rounded-lg bg-white border border-gray-100 p-3'>
                                  <p className='text-xs text-muted-foreground'>Total Calls</p>
                                  <p className='text-xl font-bold text-brand-navy mt-0.5'>{agent.call_count}</p>
                                </div>
                                {agent.avg_talk_ratio != null && (
                                  <div className='rounded-lg bg-white border border-gray-100 p-3'>
                                    <p className='text-xs text-muted-foreground'>Talk Ratio</p>
                                    <p className='text-xl font-bold text-brand-navy mt-0.5'>{agent.avg_talk_ratio}%</p>
                                  </div>
                                )}
                                {agent.avg_call_duration != null && (
                                  <div className='rounded-lg bg-white border border-gray-100 p-3'>
                                    <p className='text-xs text-muted-foreground'>Avg Duration</p>
                                    <p className='text-xl font-bold text-brand-navy mt-0.5'>{formatDuration(agent.avg_call_duration)}</p>
                                  </div>
                                )}
                                <div className='rounded-lg bg-white border border-gray-100 p-3'>
                                  <p className='text-xs text-muted-foreground'>Fatal Issues</p>
                                  <p className={cn('text-xl font-bold mt-0.5',
                                    agent.total_fatal_issues > 0 ? 'text-red-500' : 'text-emerald-600'
                                  )}>{agent.total_fatal_issues}</p>
                                </div>
                                <div className='rounded-lg bg-white border border-gray-100 p-3'>
                                  <p className='text-xs text-muted-foreground'>Grade</p>
                                  <p className='text-xl font-bold text-brand-navy mt-0.5'>{agent.grade}</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                )
              })
            )}
          </div>

          {/* Footer pagination info */}
          {hasData && filteredAgents.length > 0 && (
            <div className='relative z-10 px-5 py-3 border-t text-xs text-muted-foreground bg-gray-50/40'>
              Showing {filteredAgents.length} of {agents.length} agents
            </div>
          )}
        </Card>
      </motion.div>
    </div>
  )
}
